import {
  AnalysisFeeCalculatorDTO,
  AnalysisFeePortfolioSetupDTO,
} from "@models/platform-analysis/entities/steps/fee";
import { AnalysisFeePortfolioSetupType } from "@models/platform-analysis/enums/fee/portfolioSetupType";
import {
  getExcludeFromFeeAnalysisFieldPaths,
  getPortfolioSetupFieldPath,
} from "@pages/platform-analysis/_id/steps/fee/calculator/components/portfolio-setup/utils";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

type Props = {
  itemData: AnalysisFeePortfolioSetupDTO;
  itemIndex: number;
};

export const useAnalysisFeePortfolioSetupItem = ({
  itemData,
  itemIndex,
}: Props) => {
  const formMethod = useFormContext<AnalysisFeeCalculatorDTO>();
  const { setValue, control } = formMethod;

  const watchedPortfolioSetups: AnalysisFeePortfolioSetupDTO[] | undefined =
    useWatch({
      control,
      name: "portfolioSetups",
    });

  const fieldPaths = useMemo(
    () => getPortfolioSetupFieldPath(itemIndex),
    [itemIndex],
  );

  const excludePaths = useMemo(
    () =>
      getExcludeFromFeeAnalysisFieldPaths({
        setups: watchedPortfolioSetups ?? [],
        productId: itemData?.product?.id ?? "",
      }),
    [itemData, watchedPortfolioSetups],
  );

  const watchedProductExcludedFromFeeAnalysisValue = useWatch({
    control,
    name: excludePaths,
  });

  const isLastRemainingSubProduct = useMemo(() => {
    const isRateCardSetup =
      itemData.setupType === AnalysisFeePortfolioSetupType.RateCard;

    if (isRateCardSetup) return false;

    const includedItemsCount =
      watchedProductExcludedFromFeeAnalysisValue?.filter(
        (isExcluded) => !isExcluded,
      ).length ?? 0;

    const isCurrentItemIncluded = !itemData?.excludedFromFeeAnalysis;
    const isOnlyRemainingItem = includedItemsCount === 1;

    return isCurrentItemIncluded && isOnlyRemainingItem;
  }, [itemData, watchedProductExcludedFromFeeAnalysisValue]);

  const resetItemValues = () => {
    setValue(fieldPaths.selectedPortfolioId, null, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(fieldPaths.hasAllocationError, false);
    setValue(fieldPaths.isMissingInvestment, false);
    setValue(fieldPaths.missingInvestmentTooltip, null);
    setValue(fieldPaths.isMissingInvestmentOption, false);
    setValue(fieldPaths.missingInvestmentOptionTooltip, null);
    setValue(fieldPaths.isBelowMinimumInvestmentSize, false);
    setValue(fieldPaths.belowMinimumInvestmentSizeTooltip, null);
    setValue(fieldPaths.hasUnavailableInvestmentOption, false);
    setValue(fieldPaths.unavailableInvestmentOptionTooltip, null);
    setValue(fieldPaths.hasUnavailableInvestment, false);
    setValue(fieldPaths.unavailableInvestmentTooltip, null);
    setValue(fieldPaths.hasCashNotAvailableInSuperError, false);
    setValue(fieldPaths.hasCashNotAvailableInIDPSError, false);
  };

  const excludeItemFromFeeAnalysis = () => {
    setValue(fieldPaths.excludedFromFeeAnalysis, true, {
      shouldDirty: true,
      shouldValidate: true,
    });
    resetItemValues();
  };

  const includeItemInFeeAnalysis = () => {
    setValue(fieldPaths.excludedFromFeeAnalysis, false, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return {
    formMethod,
    isLastRemainingSubProduct,
    fieldPaths,
    excludeItemFromFeeAnalysis,
    includeItemInFeeAnalysis,
    resetItemValues,
  };
};
